import React from "react";
import { KButton, KIcon } from "@kyburz/kdesign-system";
import { downloadConfigByImei } from "../../../apiCalls/config/config";
import { DeviceColumns } from "./types";

type ColumnsParams = {
  classes: { iconContainer: string };
  onNavigate: (path: string) => void;
  onDeleteClick: (imei: string) => void;
  onShowCurrentData: (imei: string, data: string) => void;
  getTariffByLicenseNumber: (license: any) => any;
};

export const getColumns = ({
  classes,
  onNavigate,
  onDeleteClick,
  onShowCurrentData,
  getTariffByLicenseNumber,
}: ColumnsParams) => [
  {
    Header: "IMEI",
    accessor: DeviceColumns.IMEI,
    width: 250,
    defaultCanSort: true,
    sticky: "left",
  },
  {
    Header: "VIN",
    accessor: DeviceColumns.Vin,
    defaultCanSort: true,
  },
  {
    Header: "Is LSVA",
    accessor: DeviceColumns.IsLSVA,
    defaultCanSort: true,
    Cell: (row) => <div>{(row.row.original.isLSVA).toString()}</div>,
  },
  {
    Header: "Hardware Type",
    accessor: DeviceColumns.Hardware,
    defaultCanSort: true,
  },
  {
    Header: "SIM Type",
    accessor: DeviceColumns.SIMType,
    defaultCanSort: true,
  },
  {
    Header: "Customer",
    accessor: DeviceColumns.Customer,
    defaultCanSort: true,
  },
  {
    Header: "Assigned Vehicle",
    accessor: DeviceColumns.AssignedVehicle,
    width: 150,
    defaultCanSort: true,
  },
  {
    Header: "Vehicle Groups",
    accessor: DeviceColumns.VehicleGroups,
    width: 150,
  },
  {
    Header: "Vehicle Template",
    accessor: DeviceColumns.VehicleTemplate,
    width: 100,
  },
  {
    Header: "Vehicle Model",
    accessor: DeviceColumns.VehicleModel,
    defaultCanSort: true,
  },
  {
    Header: "Vehicle Brand",
    accessor: DeviceColumns.VehicleBrand,
    defaultCanSort: true,
  },
  {
    Header: "Vehicle Type",
    accessor: DeviceColumns.VehicleType,
    defaultCanSort: true,
  },
  {
    Header: "Template",
    accessor: DeviceColumns.Template,
    defaultCanSort: true,
  },
  {
    Header: "Tacho Support",
    accessor: DeviceColumns.TachoSupport,
    defaultCanSort: true,
  },
  {
    Header: "SMS Response",
    accessor: DeviceColumns.SMSResponse,
    defaultCanSort: true,
    disableFilters: true,
  },
  {
    disableFilters: true,
    Header: "SMS Response Datetime",
    accessor: DeviceColumns.SMSResponseDatetime,
    defaultCanSort: true,
    width: 200,
  },
  {
    defaultCanSort: true,
    Header: "Device State",
    accessor: DeviceColumns.DeviceState,
  },
  {
    defaultCanSort: true,
    Header: "Fsm State",
    accessor: DeviceColumns.FSMState,
  },
  {
    defaultCanSort: true,
    Header: "FW Upload Status",
    accessor: DeviceColumns.FWUploadStatus,
  },
  {
    disableFilters: true,
    Header: "Status Date (UTC)",
    accessor: DeviceColumns.FWDatetime,
  },
  {
    Header: "FW Version",
    accessor: DeviceColumns.FWVersion,
    defaultCanSort: true,
  },
  {
    Header: "Config Name",
    accessor: DeviceColumns.ConfigName,
    width: 200,
    defaultCanSort: true,
  },
  {
    Header: "Download Config",
    accessor: DeviceColumns.DownloadConfig,
    defaultCanSort: false,
    Cell: (row) => (
      <div>
        <KButton
          size="small"
          theme="secondary"
          onClick={() => {
            downloadConfigByImei(row.row.original.imei).then((res) => {
              if (res.data) {
                const href = window.URL.createObjectURL(res?.data);
                const link = document.createElement("a");
                link.href = href;
                link.setAttribute("download", `${row.row.original.imei}.config`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }
            });
          }}
        >
          Download
        </KButton>
      </div>
    ),
  },
  {
    defaultCanSort: true,
    Header: "Config Upload Status",
    accessor: DeviceColumns.ConfigUploadStatus,
    width: 200,
  },
  {
    disableFilters: true,
    Header: "Config Status Date (UTC)",
    accessor: DeviceColumns.ConfigDateTime,
    width: 200,
  },
  {
    disableFilters: true,
    Header: "Current Data Datetime",
    accessor: DeviceColumns.CurrentDataDatetime,
    width: 200,
    defaultCanSort: true,
  },
  {
    disableFilters: true,
    Header: "Current data",
    accessor: DeviceColumns.CurrentData,
    Cell: (row) => (
      <div>
        <KButton
          size="small"
          theme="secondary"
          onClick={() => onShowCurrentData(row.row.original.imei, row.row.original.currentData)}
        >
          Show
        </KButton>
      </div>
    ),
  },
  {
    Header: "Billing Tariff Name",
    accessor: (row) => getTariffByLicenseNumber(row.billingTariff),
    defaultCanSort: false,
    disableFilters: true,
    id: "billingTariffLabel",
  },
  {
    Header: "Billing Tariff License",
    accessor: DeviceColumns.BillingTariff,
    disableFilters: false,
    defaultCanSort: true,
    id: "billingTariff",
    width: 200,
  },
  {
    Header: "SIM GPRS Last Connection Start",
    accessor: DeviceColumns.SIMGPRSLastConnectionStart,
    defaultCanSort: true,
    disableFilters: true,
  },
  {
    Header: "SIM GPRS Last Connection Stop",
    accessor: DeviceColumns.SIMGPRSLastConnectionStop,
    defaultCanSort: true,
    disableFilters: true,
  },
  {
    Header: "SIM International Directory Number",
    accessor: DeviceColumns.SIMMsisdn,
    defaultCanSort: true,
  },
  {
    Header: "SIM Life Cycle Status",
    accessor: DeviceColumns.SIMLifeCycleStatus,
    defaultCanSort: true,
  },
  {
    Header: "SIM Commercial Group",
    accessor: DeviceColumns.SIMCommercialGroup,
    defaultCanSort: true,
  },
  {
    Header: "SIM Consumption Monthly Data",
    accessor: DeviceColumns.SIMConsumptionMonthlyData,
    defaultCanSort: true,
    disableFilters: true,
  },
  {
    Header: "Actions",
    Cell: (row) => (
      <div style={{ textAlign: "center" }}>
        <div
          className={classes.iconContainer}
          style={{ marginRight: "16px" }}
          onClick={() => onNavigate(`/config/devices/device/${row.row.original.imei}/overview`)}
        >
          <KIcon icon="eye" color="#383F51" size={20} />
        </div>
        <div
          className={classes.iconContainer}
          onClick={() => onDeleteClick(row.row.original.imei)}
        >
          <KIcon icon="trash-2" color="#383F51" size={20} />
        </div>
      </div>
    ),
    width: 100,
    sticky: "right",
  },
];